import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Input, Textarea, Select } from '@components';
import { SMSTemplate, CreateSMSTemplateData, UpdateSMSTemplateData } from '../types';
import { useTemplateModal } from '../hooks/useTemplateModal';

interface SMSTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateSMSTemplateData | UpdateSMSTemplateData) => Promise<void>;
  template?: SMSTemplate | null;
  loading?: boolean;
}

export const SMSTemplateModal: React.FC<SMSTemplateModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  template,
  loading = false,
}) => {
  const { t } = useTranslation();
  const { formData, errors, handleInputChange, handleSubmit, resetForm } = useTemplateModal({
    onSubmit,
    loading,
  });

  useEffect(() => {
    if (template) {
      resetForm({
        title: template.title,
        content: template.content,
        status: template.status,
      });
    } else {
      resetForm({
        title: '',
        content: '',
        status: 'active',
      });
    }
  }, [template, isOpen, resetForm]);

  const isEdit = !!template;
  const characterCount = (formData.content as string)?.length || 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('communications.editSMSTemplate') : t('communications.createSMSTemplate')}
      size="lg"
    >
      <form onSubmit={e => handleSubmit(e, ['title', 'content'])} className="space-y-6">
        <div>
          <label
            htmlFor="title"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t('communications.templateTitle')}
          </label>
          <Input
            id="title"
            type="text"
            value={(formData.title as string) || ''}
            onChange={e => handleInputChange('title', e.target.value)}
            placeholder={t('communications.enterTemplateTitle')}
            errorText={errors.title}
            required
          />
        </div>

        <div>
          <label
            htmlFor="content"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t('communications.smsContent')}
          </label>
          <Textarea
            id="content"
            value={(formData.content as string) || ''}
            onChange={e => handleInputChange('content', e.target.value)}
            placeholder={t('communications.enterSmsMessageContent')}
            rows={4}
            errorText={errors.content}
            required
          />
          <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('communications.characterCount', { current: characterCount, max: 160 })}
          </div>
        </div>

        <div>
          <label
            htmlFor="status"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t('common.status')}
          </label>
          <Select
            id="status"
            value={(formData.status as string) || 'active'}
            onChange={value => handleInputChange('status', Array.isArray(value) ? value[0] : value)}
            options={[
              { value: 'active', label: t('communications.active') },
              { value: 'inactive', label: t('common.inactive') },
            ]}
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
            {t('communications.cancel')}
          </Button>
          <Button type="submit" variant="gradient-multi" loading={loading} disabled={loading}>
            {isEdit ? t('communications.updateTemplate') : t('communications.createTemplate')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
